// Hero inspection panel for the chromium publish flow.
//
// Mirrors the data-confident profiles hero: a wide, animated card
// keyed on the resolved version. Renders the picked version with
// large monospaced typography, the upstream meta (channel/major,
// publishedAt, asset size), and a destructive Alert when the version
// is already in the manifest so the operator can't miss it before
// hitting the commit button.

import { Alert, AlertDescription, AlertTitle } from "@veil/ui/components/alert";
import { Badge } from "@veil/ui/components/badge";
import { formatBytes, formatRelative } from "@veil/ui/lib/format";
import {
	AlertTriangle,
	FileArchive,
	FileText,
	FlaskConical,
} from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { EASE_OUT_EXPO } from "@/lib/motion";
import type { UpstreamRelease } from "@/lib/releases/types.ts";

const EASE = [0.22, 1, 0.36, 1] as const;

interface Props {
	resolvedVersion: string;
	upstream: UpstreamRelease | null;
	mode: "latest" | "catalog" | "manual";
	alreadyPublished: boolean;
}

const MODE_LABEL: Record<Props["mode"], string> = {
	latest: "Latest upstream",
	catalog: "From catalog",
	manual: "Advanced (manual)",
};

export function VersionHero({
	resolvedVersion,
	upstream,
	mode,
	alreadyPublished,
}: Props) {
	const reduce = useReducedMotion();
	const major =
		upstream?.browserMajor ??
		Number.parseInt(resolvedVersion.split(".")[0] ?? "", 10);

	return (
		<motion.div
			key={resolvedVersion}
			initial={reduce ? false : { opacity: 0, scale: 0.98, y: 8 }}
			animate={{ opacity: 1, scale: 1, y: 0 }}
			exit={{ opacity: 0, scale: 0.99 }}
			transition={{ duration: 0.42, ease: EASE_OUT_EXPO }}
			className="flex flex-col gap-6 overflow-hidden rounded-2xl bg-card p-6 ring-1 ring-foreground/10"
		>
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="flex flex-col gap-1.5">
					<div className="flex flex-wrap items-center gap-2">
						<Badge variant={alreadyPublished ? "destructive" : "secondary"}>
							{MODE_LABEL[mode]}
						</Badge>
						{Number.isFinite(major) ? (
							<Badge variant="outline" className="font-mono">
								v{major}
							</Badge>
						) : null}
						{alreadyPublished ? (
							<Badge variant="warning">already published</Badge>
						) : null}
					</div>
					<p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
						Resolved version
					</p>
				</div>
				<div className="text-right">
					<div className="font-mono text-3xl font-semibold tabular-nums leading-none">
						{resolvedVersion}
					</div>
					{upstream?.publishedAt ? (
						<div className="mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">
							released {formatRelative(upstream.publishedAt)}
						</div>
					) : (
						<div className="mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">
							{mode === "manual" ? "off-catalog" : "resolved at run time"}
						</div>
					)}
				</div>
			</div>

			{alreadyPublished ? (
				<Alert variant="destructive">
					<AlertTriangle />
					<AlertTitle>This version is already in the manifest</AlertTitle>
					<AlertDescription>
						<code className="font-mono">{resolvedVersion}</code> already ships
						in chromium v{major}. Re-publishing would overwrite the existing
						build entry. Pick another version or sync the upstream catalog.
					</AlertDescription>
				</Alert>
			) : null}

			<motion.div
				initial={reduce ? false : { opacity: 0, y: 6 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.32, ease: EASE, delay: 0.08 }}
				className="grid grid-cols-1 gap-3 sm:grid-cols-3"
			>
				<MetaCell
					icon={<FileArchive />}
					label="Asset size"
					value={
						upstream?.assetSizeBytes != null
							? formatBytes(upstream.assetSizeBytes)
							: "—"
					}
				/>
				<MetaCell
					icon={<FileText />}
					label="Asset"
					value={upstream?.assetName ?? "—"}
					mono
				/>
				<MetaCell
					icon={<FlaskConical />}
					label="Tag"
					value={upstream?.tagName ?? "—"}
					mono
				/>
			</motion.div>
		</motion.div>
	);
}

function MetaCell({
	icon,
	label,
	value,
	mono = false,
}: {
	icon: React.ReactNode;
	label: string;
	value: string;
	mono?: boolean;
}) {
	return (
		<div className="flex flex-col gap-1 rounded-lg border border-border/60 bg-muted/20 p-3">
			<div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground [&>svg]:size-3">
				{icon}
				<span>{label}</span>
			</div>
			<span
				className={
					mono
						? "truncate font-mono text-xs tabular-nums text-foreground"
						: "truncate text-sm tabular-nums text-foreground"
				}
				title={value}
			>
				{value}
			</span>
		</div>
	);
}

export function HeroEmpty() {
	const reduce = useReducedMotion();
	return (
		<motion.div
			key="empty"
			initial={reduce ? false : { opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.25, ease: EASE }}
			className="flex h-full min-h-72 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-muted/20 p-12 text-center"
		>
			<FileArchive className="size-8 text-muted-foreground/60" />
			<p className="text-sm font-medium">No version selected</p>
			<p className="max-w-xs text-xs text-muted-foreground">
				Pick a mode on the left, or type a manual version. The inspection panel
				will open here.
			</p>
		</motion.div>
	);
}
